import { Grid, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/system";
import { ArrowRight } from "@pmag/icons";
import { Typography } from "@pmag/ui-components";
import { Routes } from "@src/context/router/RouterContext";
import { type GQLComponentGroupParentGenericFragment } from "@src/graphql.generated";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useRouter } from "@src/hooks/useRouter";
import { ExplosionGraphicTooltip } from "@src/pages/componentGroupOverview/components/cardGrid/explosionGraphicTooltip/ExplosionGraphicTooltip";
import { SvgModel } from "@src/pages/componentGroupOverview/components/svgModel/SvgModel";
import { type FunctionComponent } from "react";

import {
    CardImageContainer,
    ComponentGroupCode,
    ComponentGroupItem,
    ComponentGroupItemInnerContainer,
    ComponentSubGroupButton,
    ComponentSubGroupContainer,
} from "./CardGrid.styles";

export const dataTestId = {
    grid: "sparePartsFinder.overview.list.grid",
    card: "sparePartsFinder.overview.list.componentGroupCard",
    tooltip: "sparePartsFinder.overview.list.componentGroupCard.tooltip",
    subgroup: "sparePartsFinder.overview.list.componentGroupCard.subgroup",
};

export interface CardGridProps {
    brandColor?: string | null;
    componentGroupArray: GQLComponentGroupParentGenericFragment[];
}

export const CardGrid: FunctionComponent<CardGridProps> = ({ componentGroupArray, brandColor }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { setRoute } = useRouter();

    const { setComponentGroupId } = useGlobalState();

    return (
        <Grid
            container
            columns={{
                lg: 12,
                md: 12,
                sm: 8,
                xl: 12,
                xs: 4,
            }}
            spacing={4}
            data-testid={dataTestId.grid}
        >
            {componentGroupArray.map((componentGroupParent) => {
                return (
                    <ComponentGroupItem
                        item
                        key={componentGroupParent.id}
                        xs={4}
                        sm={8}
                        md={6}
                        lg={4}
                        xl={4}
                        data-id={componentGroupParent.id}
                        data-testid={dataTestId.card}
                    >
                        <ComponentGroupItemInnerContainer>
                            <CardImageContainer>
                                <ComponentGroupCode variant="heading-08">{componentGroupParent.componentGroup.code}</ComponentGroupCode>

                                {(componentGroupParent.componentAsset || componentGroupParent.componentAssetFallback) && (
                                    <SvgModel
                                        assetPath={componentGroupParent.componentAsset ?? ""}
                                        fallbackPath={componentGroupParent.componentAssetFallback ?? ""}
                                        brandColor={brandColor}
                                    />
                                )}
                            </CardImageContainer>

                            <ComponentSubGroupContainer>
                                {componentGroupParent.componentSubGroups.map((componentSubgroup) => {
                                    const componentSubGroupButton = (
                                        <ComponentSubGroupButton
                                            startIcon={<ArrowRight size="small" />}
                                            variant="text"
                                            onClick={() => {
                                                setComponentGroupId(componentSubgroup.componentGroup.id);
                                                setRoute(Routes.SubGroupDetail);
                                            }}
                                            size="small"
                                            data-testid={dataTestId.subgroup}
                                            data-component-group-template-id={componentSubgroup.id}
                                        >
                                            <Typography variant="body-short-01">{componentSubgroup.componentGroup.description}</Typography>
                                        </ComponentSubGroupButton>
                                    );
                                    if (isSmallScreen) {
                                        return componentSubGroupButton;
                                    }
                                    if (!componentSubgroup.explosionAssetMediumResolution) return null;

                                    return (
                                        <ExplosionGraphicTooltip
                                            assetPath={componentSubgroup.explosionAssetMediumResolution}
                                            key={componentSubgroup.id}
                                        >
                                            {componentSubGroupButton}
                                        </ExplosionGraphicTooltip>
                                    );
                                })}
                            </ComponentSubGroupContainer>
                        </ComponentGroupItemInnerContainer>
                    </ComponentGroupItem>
                );
            })}
        </Grid>
    );
};
